<template>
  <el-dialog
    @close="onDialogClosing"
    :width="'100%'"
    :fullscreen="true"
    :destroy-on-close="true"
    :lock-scroll="true"
    :close-on-click-modal="false"
    v-model="syncedModel"
    custom-class="dialog-ocr"
  >
    <template #title>
      <div class="flex justify-center items-center">
        <div class="label-round">
          <span v-if="scanMode === 'add'">{{ $t('scan_to_add_items') }}</span>
          <span v-else>{{ $t('scan_to_remove_items') }}</span>
        </div>
      </div>
    </template>
    <div v-loading.fullscreen="loading" v-show="!loading" class="scan-view">
      <div class="barcode-scanner">
        <div id="scan-area">
          <div class="scan-selection-camera" @click="openDialog" v-show="cameras.length > 0">
            <IconSwitch />
          </div>
          <div id="video-area">
            <video playsinline autoplay id="video"></video>
          </div>
        </div>

        <BarcodeSymbologiesSelection
          class="absolute bottom-2 z-50 left-8 flex w-52"
          @update:onSymbologiesChange="setBarcodeSymbologies"
          v-model:symbologies="symbologies"
        />
      </div>

      <!-- Display current count of scanned items -->
      <div
        class="existing-items-counter"
        :class="{ 'remove-mode': scanMode === 'remove', 'add-mode': scanMode === 'add' }"
        @click="showItemsList"
      >
        <div class="counter-message">{{ $t('number_of_scanned_items') }}</div>
        <!-- <div class="counter-badge">
          <span class="counter-value">{{ scannedValues.length }}</span>
        </div> -->

        <span class="label-box">
          {{ scannedValues.length }}
        </span>
      </div>

      <div
        class="scan-mode-switcher"
        style="display: flex; justify-content: center; align-items: center; margin-bottom: 16px;"
      >
        <el-switch
          v-model="scanMode"
          :active-value="'add'"
          :inactive-value="'remove'"
          class="ml-2"
          inline-prompt
          :active-text="$t('scan_to_add_items')"
          :inactive-text="$t('scan_to_remove_items')"
          style="--el-switch-on-color: #13ce66; --el-switch-off-color: #ff4949"
        />
      </div>

      <!-- Error message display -->
      <div v-if="validationError && scanMode === 'add'" class="error-msg-container">
        <span class="error-msg">{{ validationError }}</span>
      </div>

      <div class="step-button">
        <el-button
          :disabled="!valueCode || hasValidationError"
          circle
          type="primary"
          class="submit-button"
          @click="onDialogClosing"
        >
          OK
        </el-button>
      </div>
    </div>
    <SelectionCameraPopup v-model:selectedCamera="selectedCamera" :cameras="cameras" v-model:isShow="isOpenDialog" />

    <!-- Items List Popup -->
    <el-dialog
      v-model="showItemsListDialog"
      :lock-scroll="true"
      center
      width="300px"
      top="0"
      :show-close="false"
      :destroy-on-close="false"
      :title="$t('list_scanned_items')"
      custom-class="items-list-dialog"
      :append-to-body="true"
    >
      <div class="items-list-container">
        <div v-if="scannedValues.length === 0" class="empty-state">
          <p>No items scanned yet</p>
        </div>
        <div v-else class="items-list">
          <div v-for="(item, index) in scannedValues" :key="index" class="barcode-item-wrapper">
            <div class="barcode-label-container">
              <span class="item-text">{{ item.value }}</span>

              <div class="item-actions">
                <span class="item-index">#{{ index + 1 }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button @click="showItemsListDialog = false">Close</el-button>
      </template>
    </el-dialog>
  </el-dialog>
</template>

<script lang="ts">
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import BarcodeReaderMixin from '@/components/mixins/BarcodeReaderMixin.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import { BARCODE_SCAN } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { promptVideoPermission } from '@/utils/helpers'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import 'cropperjs/dist/cropper.css'
import { Barcode, BarcodeCapture } from 'scandit-web-datacapture-barcode'
import { EBCRType } from 'smartbarcode-web-core/src/utils/enums/index'
import { IListStringItem, IProject } from 'smartbarcode-web-core/src/utils/types'
import { mixins, Options } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'

// Mixins
@Options({
  components: {
    BarcodeSymbologiesSelection,
    IconSwitch,
    SelectionCameraPopup,
  },
  emits: ['update:modelValue', 'update:confirm'],
  inheritAttrs: false,
  name: 'ScanComponent',
})
export default class ListStringScanComponent extends mixins(BarcodeReaderMixin) {
  @Prop({ type: Boolean, default: false }) readonly modelValue!: boolean
  @Prop({ type: Array, default: [] }) readonly scannedValues!: IListStringItem[]
  @Prop({ default: EBCRType.DEFAULT }) customBarcodECameraReaderType!: EBCRType // This will override customBarcodECameraReaderType from mixins
  @Prop({ type: [String, Number] }) readonly initialValue?: string | number
  @Prop({ type: Number }) readonly minValue?: number
  @Prop({ type: Number }) readonly maxValue?: number
  codeReader: BrowserMultiFormatReader | null = new BrowserMultiFormatReader(
    undefined,
    BARCODE_SCAN.timeBetweenScansMillis as number
  )

  _barcodeDataCapture: BarcodeCapture | undefined
  loading = true
  video: HTMLVideoElement | null = null
  pictureWidth = 320
  pictureHeight = 320
  isCaptured = false
  showItemsListDialog = false

  scanMode: 'add' | 'remove' = 'add'

  confirm() {
    this.$emit('update:confirm', this.valueCode.trim(), this.scanMode)
  }

  showItemsList() {
    if (this.scannedValues.length > 0) {
      this.showItemsListDialog = true
    }
  }

  onDialogClosing() {
    this.$emit('update:modelValue', false)
  }

  get syncedModel() {
    return this.modelValue
  }

  set syncedModel(value: boolean) {
    this.$emit('update:modelValue', value)
  }

  defaultScanReader(result: Result, codeReader: BrowserMultiFormatReader) {
    this.codeReader = codeReader
    const tempResult: { [key: string]: unknown } = { ...result }
    if (!tempResult?.text) {
      return
    }
    this.handleScanResult((tempResult.text as string) || '')
  }

  scanditDataResult(symbology: Barcode) {
    if (!symbology) return
    this.handleScanResult((symbology?.data as string) || '')
  }

  async barcodeDetectorPolyFillScanReader(result: DetectedBarcode[]) {
    if (result.length < 1) return
    const firstDetectedBarcode = result[0]?.rawValue || ''
    this.handleScanResult(firstDetectedBarcode)
  }

  handleScanResult(plainText: string) {
    this.valueCode = plainText
    // Automatically call confirm when a new value is scanned
    if (plainText.trim()) {
      this.confirm()
    }
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  get project(): IProject {
    return this.$store.state.project.details
  }

  get validationError(): string {
    const length = this.scannedValues.length

    if (this.minValue !== undefined && length < this.minValue) {
      return this.$t('minimum_items_required', { minLength: this.minValue })
    }

    if (this.maxValue !== undefined && length > this.maxValue) {
      return this.$t('maximum_items_required', { maxLength: this.maxValue })
    }

    return ''
  }

  get hasValidationError(): boolean {
    return !!this.validationError
  }

  async mounted() {
    // Disable multiple scan for immediate confirmation
    this.isShowMultipleScan = false
    this.isMultipleScan = false

    // Set initial value if provided
    if (this.initialValue) {
      this.valueCode = this.initialValue.toString()
    }

    await this.initCameraSelection()
    if (this.isCustomVideoReader) {
      await promptVideoPermission()
    }

    await this.setupCameraSetting()
  }

  get isCustomVideoReader() {
    return !(this.customBarcodECameraReaderType === EBCRType.SCANDIT)
  }

  async setupCameraSetting() {
    switch (this.barcodeReaderType as EBCRType) {
      case EBCRType.SCANDIT:
        await this.initScanditReader()
        break
      case EBCRType.BARCODE_DETECTION:
        await this.initBarcodeReaderPolyFill()
        break
      default:
        await promptVideoPermission()
        await this.initZxingReader()
        break
    }
  }

  get isScanditReader() {
    return this.customBarcodECameraReaderType === EBCRType.SCANDIT
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }
    if (this.selectedCamera) {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            width: { ideal: 1000 },
            height: { ideal: 1000 },
            frameRate: {
              ideal: 30,
              max: 60,
            },
            aspectRatio: 1,
            deviceId: { exact: this.selectedCamera },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          console.log('error', e)
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    } else {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'environment',
            aspectRatio: 1,
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }

  async handleMultipleScan(plainText: string) {
    if (this.isHandleMultipleScan) {
      return
    }

    // For single scan mode, just confirm immediately
    this.valueCode = plainText
    this.confirm()
  }
}
</script>
<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';

.margin-ocr {
  margin-top: 10px;
}

.margin-top-reader {
  margin-top: 16px;
}

.step-button {
  display: flex;
  padding-left: 25%;
  padding-right: 25%;
}

.media-container {
  width: 100%;
  position: relative;
  height: 40vh;

  video {
    top: 0;
    left: 0;
    width: 100%;
    object-fit: cover;
    height: 40vh;
  }
}

.media-container:deep() {
  &.scandit-container {
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
  }
}

.dectect-input {
  padding: 0 30px 0 30px;
  text-align-last: center;
}

.scan-view {
  text-align: center;
}

.message-wrapper {
  margin-top: 10px;
  word-break: break-word;
  position: absolute;
  z-index: 100;
  color: $pure-white;
  text-align: center;
  padding: 0 2rem;
  width: 100%;
  box-sizing: border-box;
  font-size: 0.85rem;

  &.top {
    margin-top: 10px;

    > div {
      padding: 8px 12px;
      background: rgb(0, 0, 0, 0.7);
      border-radius: 5px;
    }
  }
}

.image-container {
  max-width: 100%;
  height: 40vh;
  display: none;
  object-fit: cover;
}

.img {
  max-width: 100%;
  height: 40vh;
}

.barcode-scanner {
  height: 60vh;
  background: rgb(32, 32, 32);
  position: relative;
}

.barcode-scanner video {
  width: 100%;
  height: 100%;
}

.existing-items-counter {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  margin: 16px 20px;
  background-color: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  cursor: pointer;

  &.add-mode {
    background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
    border-color: #409eff;

    .counter-badge {
      background-color: #409eff;
    }
  }

  &.remove-mode {
    background: linear-gradient(135deg, #fef2f2 0%, #fef7f7 100%);
    border-color: #f56c6c;

    .counter-badge {
      background-color: #f56c6c;
    }
  }

  .counter-message {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    flex: 1;
    margin-right: 12px;
    line-height: 1.4;
  }

  .counter-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    height: 32px;
    border-radius: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;

    .counter-value {
      color: white;
      font-weight: 600;
      font-size: 14px;
      text-align: center;
    }
  }
}

// Items List Popup Styles
:deep(.items-list-dialog) {
  .el-dialog__header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    border-radius: 8px 8px 0 0;
  }

  .el-dialog__title {
    font-weight: 600;
    color: #333;
  }
}

.error-msg-container {
  text-align: center;
  padding: 16px 20px 8px 20px;
}

.error-msg {
  color: #f56c6c;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  background-color: rgba(245, 108, 108, 0.1);
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(245, 108, 108, 0.3);
}

.items-list-container {
  max-height: 60vh;
  overflow-y: auto;
  .empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #666;
    font-style: italic;
  }

  .items-list {
    display: flex;
    flex-direction: column;
    gap: 0;

    .barcode-item-wrapper {
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      position: relative;
    }

    .barcode-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      margin-bottom: 8px;
      background-color: #f8f9fa;
      border-radius: 6px;
      border: 1px solid #e9ecef;

      .drag-handle {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        margin-right: 8px;
        cursor: grab;
        color: #666;
        border-radius: 6px;
        transition: all 0.2s ease;
        touch-action: none; // Prevent scrolling when touching the handle

        &:hover {
          background-color: #e9ecef;
          color: #333;
        }

        &:active {
          cursor: grabbing;
          background-color: #dee2e6;
        }

        // Mobile responsive - larger touch target
        @media (max-width: 768px) {
          width: 40px;
          height: 40px;

          svg {
            width: 20px;
            height: 20px;
          }
        }

        svg {
          width: 16px;
          height: 16px;
          pointer-events: none; // Prevent svg from interfering with touch events
        }
      }

      .item-text {
        flex: 1;
        font-weight: 500;
        color: #333;
        word-break: break-word;
        margin-left: 8px;
      }

      .item-actions {
        margin-left: 5px;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .item-index {
        font-size: 12px;
        color: #666;
        background-color: #e9ecef;
        padding: 2px 8px;
        border-radius: 12px;
      }
    }
  }

  .barcode-label-container {
    .item-text {
      flex: 1;
    }

    .item-actions {
      margin-left: 5px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .item-index {
      color: #959595;
      font-size: 12px;
      background-color: #e9ecef;
      padding: 2px 8px;
      border-radius: 12px;
    }
  }
}
</style>
